/* Core */
import { useContext } from "react";
import { Outlet, Route, Routes } from "react-router-dom";

/* Application */
import Prefetch from "state/application/Prefetch";

/* Auth */
import ProtectedRoute from "components/ProtectedRoute";
import ChangePassword from "pages/Auth/ChangePassword";
import ForgotPassword from "pages/Auth/ResetPasswordRequest";
import SignIn from "pages/Auth/SignIn";
import SignOut from "pages/Auth/SignOut";
import SignUp from "pages/Auth/SignUp";

/* Dashboard */
import Default from "pages/Dashboard";

/* Simulation */
import Simulation from "pages/Simulation";
import RunOperations from "pages/RunOperations";

/* Structure */
import Structure from "pages/Structure";

/* Community */
import ListCommunities from "pages/ManageCommunities/List";
import SingleCommunity from "pages/ManageCommunities/Single";
import ManageCoefficients from "pages/ManageCoefficients";
import ManageBilateralPrices from "pages/ManageBilateralPrices";
import Simulator from "pages/Simulator";
/* Members */
import ListMembers from "pages/ManageMembers/List";
import NewMember from "pages/ManageMembers/New";
import SingleMember from "pages/ManageMembers/Single";

/* Resources */
import ListResources from "pages/ManageResources/List";
import NewResource from "pages/ManageResources/New";
import SingleResource from "pages/ManageResources/Single";

/* Meters */
import ListMeters from "pages/ManageMeters/List";
import SingleMeter from "pages/ManageMeters/Single";

/* RESP */
import ListRESP from "pages/ManageRESP";
import VersionEditorPage from "pages/BusinessValidity/Editor";
import VersionHistoryPage from "pages/BusinessValidity/History";

/* Contracts */
import SingleContract from "pages/ManageContracts/Single";
import ListAllContracts from "pages/ManageContracts/ListAll";
import UserSettings from "pages/UserSettings";

/* ERSE */
import SingleERSE from "pages/ManageERSE/Single";

/* OMIE */
import ReadOMIE from "pages/ReadOMIE";

/* Loads */
import ListLoads from "pages/ManageLoads";
import ListForecastLoads from "pages/ManageLoadsForecast";
import ManageMeasurements from "pages/ManageMeasurements";
import ManageEVs from "pages/ManageEVs";
import ManageEVBatteryStateLoads from "pages/ManageEVs/EVBatteryStateLoads";
import ManageBatteryStateLoads from "pages/ManageBatteries/StorageBatteryStateLoads";
import ManageEVDrivingLoads from "pages/ManageEVs/DrivingLoads";
import ManageEVChargingIntervalsForecast from "pages/ManageEVs/ChargingIntervalForecast";
import ManageEWHUsage from "pages/ManageEWH/Usage";
import ManageHP from "pages/ManageHP";

/* Flexibility */
import ListFlexibility from "pages/ReadFlexibility/List";
/* DSO */
import ListDSO from "pages/ManageDSO";

/* Market */
import ReadMarket from "pages/ReadMarket";

/* Settlement */
import ListResults from "pages/ReadSettlement";

/* Errors */
import PasswordReset from "pages/Auth/ResetPassword";
import Error404 from "pages/Error/404";

/* Support */
import About from "pages/Support/About";
import Contacts from "pages/Support/Contacts";
import FAQ from "pages/Support/FAQ";
import PrivacyPolicy from "pages/PrivacyPolicy";
import TermsAndConditions from "pages/TermsAndConditions";

/* Admin Panel */
import AdminPanel from "pages/Admin/Panel";
import ManageOpportunityCosts from "pages/ManageOpportunityCosts";
import ManageBilateralEnergyAllocations from "pages/ManageBilateralEnergyAllocations";

/* Energy Management */
import ManageEnergyManagement from "pages/ManageEnergyManagement";

/* RBAC */
import { AbilityContext } from "context/Ability.context";
import RoleRoute from "components/RoleRoute";

function CommunityWrapper() {
  return <Outlet />;
}

export default function Router() {
  const ability = useContext(AbilityContext);

  return (
    <Routes>
      <Route path="/" element={<Outlet />}>
        {/* --------------- Public routes --------------- */}
        <Route index element={<SignIn />} />
        <Route path="login/:user" element={<SignIn />} />
        <Route path="login" element={<SignIn />} />
        <Route path="signin" element={<SignIn />} />
        <Route path="signout" element={<SignOut />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="signup" element={<SignUp />} />
        <Route path="change-password/:token" element={<ChangePassword />} />
        <Route path="password_reset/:token" element={<PasswordReset />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="terms-and-conditions" element={<TermsAndConditions />} />
        {/* --------------- Protected Routes --------------- */}
        <Route element={<ProtectedRoute />}>
          <Route path="settings" element={<UserSettings />} />
          <Route path="communities">
            <Route index element={<ListCommunities />} />
            <Route path="simulator" element={<Simulator />} />
            <Route path="admin" element={<AdminPanel />} />
            <Route path="admin/tariffs/:validity" element={<SingleERSE propKey="tariffs" />} />
            <Route path="admin/losses/:validity" element={<SingleERSE propKey="losses" />} />
            <Route element={<Prefetch />}>
              <Route path=":id" element={<CommunityWrapper />}>
                <Route index element={<Default />} />

                {/* ── Admin-only routes ─────────────────────────────────────── */}
                <Route element={<RoleRoute allowed={ability.can("manage", "all")} />}>
                  <Route path="simulation" element={<Simulation />} />
                  <Route path="operations/run" element={<RunOperations />} />
                  <Route path="configuration" element={<SingleCommunity />} />
                  <Route path="coefficients" element={<ManageCoefficients />} />
                  <Route path="bilateral-prices" element={<ManageBilateralPrices />} />
                  <Route path="opportunity_costs" element={<ManageOpportunityCosts />} />
                  <Route
                    path="bilateral_energy_allocations"
                    element={<ManageBilateralEnergyAllocations />}
                  />
                  <Route path="structure" element={<Structure />} />
                  <Route path="members">
                    <Route index element={<ListMembers />} />
                    <Route path=":id" element={<SingleMember />} />
                    <Route path="new" element={<NewMember />} />
                  </Route>
                  <Route path="infrastructure/RESP" element={<ListRESP />} />
                  <Route path="dso">
                    <Route index element={<ListDSO />} />
                  </Route>
                  <Route path="erse/tariffs/:validity" element={<SingleERSE propKey="tariffs" />} />
                  <Route path="erse/losses/:validity" element={<SingleERSE propKey="losses" />} />
                  <Route path="energymanagement">
                    <Route index element={<ManageEnergyManagement />} />
                  </Route>
                </Route>

                {/* ── Member-accessible routes ────────────────────────────────── */}
                <Route path="versions/:entityType/:entityId" element={<VersionHistoryPage />} />
                <Route path="versions/:entityType/:entityId/new" element={<VersionEditorPage />} />
                <Route
                  path="versions/:entityType/:entityId/:versionId/edit"
                  element={<VersionEditorPage />}
                />
                <Route path="infrastructure">
                  <Route index element={<ListResources />} />
                  <Route path="resources">
                    <Route index element={<ListResources />} />
                    <Route path=":id" element={<SingleResource />} />
                    <Route path="new" element={<NewResource />} />
                  </Route>
                  <Route path="meters">
                    <Route index element={<ListMeters />} />
                    <Route path=":id" element={<SingleMeter />} />
                  </Route>
                  <Route path="contracts">
                    <Route index element={<ListAllContracts />} />
                    <Route path="new" element={<SingleContract />} />
                    <Route path=":id" element={<SingleContract />} />
                  </Route>
                </Route>
                <Route path="omie">
                  <Route index element={<ReadOMIE />} />
                </Route>
                <Route path="loads">
                  <Route index element={<ListLoads />} />
                  <Route path="loadsForecast" element={<ListForecastLoads />} />
                  <Route path="measurements" element={<ManageMeasurements />} />
                  <Route path="ev" element={<ManageEVs />} />
                  <Route path="evbatteries" element={<ManageEVBatteryStateLoads />} />;
                  <Route path="batteries" element={<ManageBatteryStateLoads />} />;
                  <Route path="evdrivingconsumption" element={<ManageEVDrivingLoads />} />;
                  <Route
                    path="evforecastchargingintervals"
                    element={<ManageEVChargingIntervalsForecast />}
                  />
                  <Route path="ewh_usage" element={<ManageEWHUsage />} />
                  <Route path="hp" element={<ManageHP />} />
                </Route>
                <Route path="flexibility">
                  <Route path="bids" element={<ListFlexibility />} />;
                </Route>
                <Route path="market">
                  <Route index element={<ReadMarket />} />
                </Route>
                <Route path="settlement">
                  <Route index element={<ListResults />} />
                </Route>
                <Route path="about">
                  <Route index element={<About />} />
                </Route>
                <Route path="faq">
                  <Route index element={<FAQ />} />
                </Route>
                <Route path="contacts">
                  <Route index element={<Contacts />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Error404 />} />
    </Routes>
  );
}
